import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  fetchPartnerDealer,
  fetchPartnerDealerInventory,
  fetchPartnerInventoryRolloutConfig,
  runPartnerDealerListingAction,
} from "../../services/automapPartnerDealerApi";
import PartnerGroupedInventoryView from "../../components/partner/inventory/PartnerGroupedInventoryView";
import PartnerInventoryViewToggle from "../../components/partner/inventory/PartnerInventoryViewToggle";
import { usePartnerPortalBase } from "../../components/partner/PartnerPortalBaseProvider";
import {
  PARTNER_INVENTORY_ISSUE_FILTERS,
  partnerInventoryIssueLabel,
} from "../../constants/partnerInventoryHealthConstants";
import {
  PARTNER_NORMALIZATION_SOURCE_FILTERS,
  PARTNER_NORMALIZATION_STATUS_FILTERS,
} from "../../constants/partnerNormalizationReviewConstants";
import { isGroupedStatusSupported } from "../../utils/partnerInventoryFormatters";
import { partnerListingDisplayTitle } from "../../utils/partnerListingDisplay";
import DealerListingPublicAction from "../../components/dealer/DealerListingPublicAction";
import { useLocale } from "../../intl";
import {
  hasStrongVehicleIdentityCoverage,
  parseInventoryViewFromParams,
  PARTNER_INVENTORY_VIEW_MODES,
  resolvePartnerInventoryDefaultView,
} from "../../utils/partnerInventoryDefaultView";
import {
  getPartnerInventoryViewPreference,
  setPartnerInventoryViewPreference,
} from "../../utils/partnerInventoryViewPreferenceStorage";
import {
  PARTNER_INVENTORY_ANALYTICS_EVENTS,
  trackPartnerInventoryAnalyticsEvent,
} from "../../utils/partnerInventoryAnalytics";
import "../../styles/partnerDealers.css";

function primaryPhoto(listing) {
  const photo = listing.photos?.find((entry) => entry.isPrimary) || listing.photos?.[0];
  return photo?.url || null;
}

export default function PartnerDealerInventoryPage() {
  const { dealerId } = useParams();
  const { locale } = useLocale();
  const { path } = usePartnerPortalBase();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlView = parseInventoryViewFromParams(searchParams);
  const [rolloutConfig, setRolloutConfig] = useState(null);
  const [rolloutLoading, setRolloutLoading] = useState(true);
  const [rolloutError, setRolloutError] = useState("");
  const [dealer, setDealer] = useState(null);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [source, setSource] = useState("");
  const [normalizationStatus, setNormalizationStatus] = useState("");
  const issue = searchParams.get("issue") || "";
  const defaultAppliedRef = useRef(false);
  const viewTrackedRef = useRef(false);

  const groupedViewAvailable = rolloutConfig?.groupedViewAvailable ?? false;
  const normalizationReviewEnabled = rolloutConfig?.flags?.normalizationReviewEnabled ?? true;
  const healthNormalizationEnabled = rolloutConfig?.flags?.healthNormalizationEnabled ?? true;

  const vehicleIdentityMetrics = rolloutConfig?.vehicleIdentity || null;
  const needsReviewCount = vehicleIdentityMetrics?.needsReviewCount ?? 0;

  const inventoryView = useMemo(() => {
    if (urlView) {
      if (urlView === PARTNER_INVENTORY_VIEW_MODES.GROUPED && !groupedViewAvailable) {
        return PARTNER_INVENTORY_VIEW_MODES.FLAT;
      }
      return urlView;
    }

    if (!rolloutConfig) return PARTNER_INVENTORY_VIEW_MODES.FLAT;

    const resolved = resolvePartnerInventoryDefaultView({
      userPreference: getPartnerInventoryViewPreference(dealerId),
      dealerSetting: rolloutConfig.dealerInventoryViewSetting,
      groupedViewAvailable,
      groupedDefaultEnabled: rolloutConfig.flags?.groupedViewDefaultEnabled,
      rolloutStage: rolloutConfig.rolloutStage,
      vehicleIdentityHealth: rolloutConfig.vehicleIdentity,
      rolloutDefaultView: rolloutConfig.defaultView,
    });
    return resolved.view;
  }, [dealerId, groupedViewAvailable, rolloutConfig, urlView]);

  const groupedDefaultReason = rolloutConfig?.defaultView?.reason || null;

  const loadRolloutConfig = useCallback(async () => {
    try {
      setRolloutLoading(true);
      setRolloutError("");
      const result = await fetchPartnerInventoryRolloutConfig({
        dealerId,
        view: urlView || undefined,
        preference: getPartnerInventoryViewPreference(dealerId) || undefined,
      });
      setRolloutConfig(result.rollout || null);
    } catch (err) {
      setRolloutError(err.message || "Unable to load inventory rollout settings.");
      setRolloutConfig(null);
    } finally {
      setRolloutLoading(false);
    }
  }, [dealerId, urlView]);

  const loadDealer = useCallback(async () => {
    try {
      const result = await fetchPartnerDealer(dealerId);
      setDealer(result.dealer || null);
    } catch {
      // Dealer header can remain empty; child views surface errors.
    }
  }, [dealerId]);

  const loadInventory = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const result = await fetchPartnerDealerInventory(dealerId, {
        search: search.trim() || undefined,
        status: status || undefined,
        issue: issue || undefined,
        sort: "updated_desc",
      });
      setDealer(result.dealer);
      setListings(result.listings || []);
    } catch (err) {
      setError(err.message || "Unable to load inventory.");
      setDealer(null);
      setListings([]);
    } finally {
      setLoading(false);
    }
  }, [dealerId, issue, search, status]);

  useEffect(() => {
    loadRolloutConfig();
  }, [loadRolloutConfig]);

  useEffect(() => {
    if (rolloutLoading || urlView || defaultAppliedRef.current || !rolloutConfig) return;

    const resolved = resolvePartnerInventoryDefaultView({
      userPreference: getPartnerInventoryViewPreference(dealerId),
      dealerSetting: rolloutConfig.dealerInventoryViewSetting,
      groupedViewAvailable,
      groupedDefaultEnabled: rolloutConfig.flags?.groupedViewDefaultEnabled,
      rolloutStage: rolloutConfig.rolloutStage,
      vehicleIdentityHealth: rolloutConfig.vehicleIdentity,
      rolloutDefaultView: rolloutConfig.defaultView,
    });

    defaultAppliedRef.current = true;
    if (resolved.view === PARTNER_INVENTORY_VIEW_MODES.GROUPED) {
      const params = new URLSearchParams(searchParams);
      params.set("view", "grouped");
      setSearchParams(params, { replace: true });
    }
  }, [
    dealerId,
    groupedViewAvailable,
    rolloutConfig,
    rolloutLoading,
    searchParams,
    setSearchParams,
    urlView,
  ]);

  useEffect(() => {
    if (rolloutLoading) return;

    if (inventoryView === PARTNER_INVENTORY_VIEW_MODES.FLAT) {
      loadInventory();
      return;
    }
    loadDealer();
    setLoading(false);
    setError("");
    setListings([]);
  }, [inventoryView, loadDealer, loadInventory, rolloutLoading]);

  useEffect(() => {
    if (rolloutLoading || viewTrackedRef.current) return;
    viewTrackedRef.current = true;
    trackPartnerInventoryAnalyticsEvent(PARTNER_INVENTORY_ANALYTICS_EVENTS.VIEW_LOADED, {
      dealerId,
      viewMode: inventoryView,
      totalListings: vehicleIdentityMetrics?.totalListings ?? null,
      makeModelCoverage: vehicleIdentityMetrics?.makeModelCoverage ?? null,
      needsReviewRate: vehicleIdentityMetrics?.needsReviewRate ?? null,
      groupedDefaultReason,
    });
  }, [
    dealerId,
    groupedDefaultReason,
    inventoryView,
    rolloutLoading,
    vehicleIdentityMetrics,
  ]);

  const handleViewChange = (nextView) => {
    setPartnerInventoryViewPreference(dealerId, nextView);
    const params = new URLSearchParams(searchParams);
    if (nextView === PARTNER_INVENTORY_VIEW_MODES.GROUPED) params.set("view", "grouped");
    else params.set("view", "flat");
    setSearchParams(params, { replace: true });

    trackPartnerInventoryAnalyticsEvent(PARTNER_INVENTORY_ANALYTICS_EVENTS.VIEW_TOGGLED, {
      dealerId,
      viewMode: nextView,
      makeModelCoverage: vehicleIdentityMetrics?.makeModelCoverage ?? null,
      needsReviewRate: vehicleIdentityMetrics?.needsReviewRate ?? null,
    });
  };

  const handleIssueChange = (nextIssue) => {
    const params = new URLSearchParams(searchParams);
    if (nextIssue) params.set("issue", nextIssue);
    else params.delete("issue");
    setSearchParams(params, { replace: true });
  };

  const handleAction = async (listingId, action) => {
    try {
      await runPartnerDealerListingAction(dealerId, listingId, action);
      await loadInventory();
    } catch (err) {
      setError(err.message || "Unable to run listing action.");
    }
  };

  const isLiveDealer = dealer?.dataSource === "live_profile";
  const groupedStatusSupported = isGroupedStatusSupported(status);
  const showGroupedCleanupNudge =
    inventoryView === PARTNER_INVENTORY_VIEW_MODES.GROUPED &&
    groupedViewAvailable &&
    needsReviewCount > 0 &&
    !hasStrongVehicleIdentityCoverage(rolloutConfig?.vehicleIdentity);

  return (
    <div className="partner-dealers-page panel-card">
      <header className="partner-dealers-page__header">
        <div>
          <Link className="cv-button-link" to={path("dealers")}>
            ← All dealers
          </Link>
          {healthNormalizationEnabled ? (
            <Link className="cv-button-link" to={path("inventory-health")}>
              Inventory health
            </Link>
          ) : null}
          {normalizationReviewEnabled ? (
            <Link className="cv-button-link" to={path(`dealers/${dealerId}/inventory/normalization`)}>
              Vehicle names
            </Link>
          ) : null}
          <h2>Dealer inventory</h2>
          <p className="muted">
            {dealer?.businessName ? (
              <>
                Managing listings for <strong>{dealer.businessName}</strong>
                {isLiveDealer ? " from live market inventory." : " in the dealer portal."}
              </>
            ) : (
              "Review and edit listings on behalf of this dealership."
            )}
          </p>
          {issue && inventoryView === PARTNER_INVENTORY_VIEW_MODES.FLAT ? (
            <p className="partner-inventory-filter-banner">
              Filtered by <strong>{partnerInventoryIssueLabel(issue)}</strong>
              {" · "}
              <button type="button" className="cv-button-link" onClick={() => handleIssueChange("")}>
                Clear filter
              </button>
            </p>
          ) : null}
        </div>
        {!isLiveDealer ? (
          <Link className="midpoint-share-button" to={path(`dealers/${dealerId}/inventory/new`)}>
            Add listing
          </Link>
        ) : null}
      </header>

      {rolloutError ? <p className="admin-data-health-error">{rolloutError}</p> : null}

      <PartnerInventoryViewToggle
        value={inventoryView}
        onChange={handleViewChange}
        groupedAvailable={groupedViewAvailable}
      />

      {showGroupedCleanupNudge ? (
        <div className="partner-inventory-rollout-nudge">
          <p className="muted">
            Grouped view is available, but <strong>{needsReviewCount}</strong> listing
            {needsReviewCount === 1 ? "" : "s"} still need vehicle name review.
          </p>
          <div className="partner-inventory-rollout-nudge__actions">
            {normalizationReviewEnabled ? (
              <Link className="cv-button-link" to={path(`dealers/${dealerId}/inventory/normalization`)}>
                Fix vehicle names
              </Link>
            ) : null}
            {healthNormalizationEnabled ? (
              <Link
                className="cv-button-link"
                to={path(`inventory-health?dealerId=${encodeURIComponent(dealerId)}`)}
              >
                View inventory health
              </Link>
            ) : null}
            <button
              type="button"
              className="cv-button-link"
              onClick={() => handleViewChange(PARTNER_INVENTORY_VIEW_MODES.FLAT)}
            >
              Back to flat list
            </button>
          </div>
        </div>
      ) : null}

      <div className="partner-dealers-page__actions">
        <input
          type="search"
          className="partner-dealers-page__search"
          placeholder="Search listings"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <select value={status} onChange={(event) => setStatus(event.target.value)}>
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="draft">Draft</option>
          <option value="paused">Paused</option>
          <option value="sold">Sold</option>
        </select>
        {inventoryView === PARTNER_INVENTORY_VIEW_MODES.FLAT ? (
          <select value={issue} onChange={(event) => handleIssueChange(event.target.value)}>
            {PARTNER_INVENTORY_ISSUE_FILTERS.map((option) => (
              <option key={option.value || "all"} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        ) : (
          <>
            <select value={source} onChange={(event) => setSource(event.target.value)}>
              {PARTNER_NORMALIZATION_SOURCE_FILTERS.map((option) => (
                <option key={option.value || "all"} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <select
              value={normalizationStatus}
              onChange={(event) => setNormalizationStatus(event.target.value)}
            >
              {PARTNER_NORMALIZATION_STATUS_FILTERS.map((option) => (
                <option key={option.value || "default"} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </>
        )}
      </div>

      {inventoryView === PARTNER_INVENTORY_VIEW_MODES.GROUPED && !groupedStatusSupported ? (
        <p className="partner-grouped-filter-note muted">
          Draft and paused filters are only available in the flat list.
        </p>
      ) : null}

      {inventoryView === PARTNER_INVENTORY_VIEW_MODES.FLAT && error ? (
        <p className="admin-data-health-error">{error}</p>
      ) : null}
      {inventoryView === PARTNER_INVENTORY_VIEW_MODES.FLAT && loading ? (
        <p className="muted">Loading inventory…</p>
      ) : null}

      {inventoryView === PARTNER_INVENTORY_VIEW_MODES.FLAT && !loading && !error && listings.length === 0 ? (
        <div className="partner-dealers-empty">
          <p className="muted">
            {issue ? "No listings match this data-health filter." : "No listings found for this dealer."}
          </p>
          {!isLiveDealer ? (
            <Link className="midpoint-share-button" to={path(`dealers/${dealerId}/inventory/new`)}>
              Add first listing
            </Link>
          ) : null}
        </div>
      ) : null}

      {inventoryView === PARTNER_INVENTORY_VIEW_MODES.FLAT && !loading && listings.length > 0 ? (
        <div className="partner-inventory-grid">
          {listings.map((listing) => (
            <article key={listing.id} className="partner-inventory-card">
              <div className="partner-inventory-card__media">
                {primaryPhoto(listing) ? (
                  <img src={primaryPhoto(listing)} alt="" />
                ) : (
                  <div className="partner-inventory-card__placeholder">No photo</div>
                )}
              </div>
              <div className="partner-inventory-card__body">
                <h3>{partnerListingDisplayTitle(listing)}</h3>
                <p className="muted">
                  <span className={`partner-dealers-badge partner-dealers-badge--${listing.status === "active" ? "active" : ""}`}>
                    {listing.status}
                  </span>
                  {listing.publicReady === false ? (
                    <span className="partner-dealers-badge partner-dealers-badge--managed">
                      {" "}
                      Not public-ready
                    </span>
                  ) : null}
                  {listing.missingPhotos ? (
                    <span className="partner-dealers-badge partner-dealers-badge--managed">
                      {" "}
                      No photo · hidden on AutoMapa
                    </span>
                  ) : null}
                  {listing.availabilityStatus ? ` · ${listing.availabilityStatus}` : ""}
                  {listing.sourceName ? ` · ${listing.sourceName}` : ""}
                </p>
                {listing.qualityIssues?.length ? (
                  <div className="partner-inventory-card__issues">
                    {listing.qualityIssues.map((issueKey) => (
                      <span key={issueKey} className="partner-inventory-issue-chip">
                        {partnerInventoryIssueLabel(issueKey)}
                      </span>
                    ))}
                  </div>
                ) : null}
                <p>
                  {listing.priceCop != null
                    ? new Intl.NumberFormat("es-CO", {
                        style: "currency",
                        currency: "COP",
                        maximumFractionDigits: 0,
                      }).format(listing.priceCop)
                    : "—"}
                </p>
                <div className="partner-inventory-card__actions">
                  <Link
                    className="cv-button-link"
                    to={path(`dealers/${dealerId}/inventory/${listing.id}/edit`)}
                  >
                    Edit
                  </Link>
                  {listing.status === "active" ? (
                    <DealerListingPublicAction
                      listing={listing}
                      locale={locale}
                      t={(key) =>
                        ({
                          "inventoryDetail.viewPublic": "View on AutoMapa",
                          "inventory.publicListingUnavailable": "Public listing unavailable",
                          "inventory.publicListingSyncPending": "Public listing is still syncing",
                        }[key] || key)
                      }
                      className="cv-button-link dealer-listing-public-action__link"
                      label="View on AutoMapa"
                      showUnavailableHint={false}
                      icon=""
                    />
                  ) : null}
                  {listing.sourceUrl ? (
                    <a className="cv-button-link" href={listing.sourceUrl} target="_blank" rel="noreferrer">
                      Source
                    </a>
                  ) : null}
                  {listing.status !== "active" ? (
                    <button
                      type="button"
                      className="cv-button-link"
                      onClick={() => handleAction(listing.id, "reactivate")}
                    >
                      Activate
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="cv-button-link"
                      onClick={() => handleAction(listing.id, "mark_sold")}
                    >
                      Mark sold
                    </button>
                  )}
                </div>
              </div>
            </article>
          ))}
        </div>
      ) : null}

      {inventoryView === PARTNER_INVENTORY_VIEW_MODES.GROUPED ? (
        <PartnerGroupedInventoryView
          dealerId={dealerId}
          path={path}
          search={search}
          status={status}
          source={source}
          normalizationStatus={normalizationStatus}
          vehicleIdentityMetrics={vehicleIdentityMetrics}
          normalizationReviewEnabled={normalizationReviewEnabled}
        />
      ) : null}
    </div>
  );
}
